import React, { useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
} from '@mui/material';

import { getUserProfile } from '../utils/api';
import SubscriptionPanel from './SubscriptionPanel';

const formatResetDate = (value) => {
  if (!value) return null;
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return null;
  return new Intl.DateTimeFormat(undefined, {
    dateStyle: 'medium',
    timeStyle: 'short',
  }).format(date);
};

const SubscriptionDialog = ({
  open,
  onClose,
  onAccessGranted,
  quota = null,
  title = 'AI generation limit reached',
  message = null,
  closeLabel = 'Back to task',
}) => {
  const [username, setUsername] = useState('');
  const [profileError, setProfileError] = useState('');
  const [loadingProfile, setLoadingProfile] = useState(false);

  useEffect(() => {
    if (!open) return undefined;
    let cancelled = false;
    setLoadingProfile(true);
    setProfileError('');
    getUserProfile()
      .then((profile) => {
        if (!cancelled) setUsername(profile?.username || '');
      })
      .catch(() => {
        if (!cancelled) {
          setProfileError('Unable to load subscription options. Please try again.');
        }
      })
      .finally(() => {
        if (!cancelled) setLoadingProfile(false);
      });
    return () => {
      cancelled = true;
    };
  }, [open]);

  const resetDate = formatResetDate(quota?.reset_at);

  return (
    <Dialog
      id="subscription-dialog"
      open={open}
      onClose={onClose}
      fullWidth
      maxWidth="md"
      scroll="paper"
    >
      <DialogTitle>{title}</DialogTitle>
      <DialogContent>
        <Box sx={{ display: 'grid', gap: 2, pt: 0.5 }}>
          <Alert severity="info">
            {message || (
              <>
                You have used all {quota?.limit ?? ''} AI task generations for this period.
                {resetDate ? ` Your credits reset ${resetDate}.` : ''}
                {' '}You can subscribe for unlimited AI generations.
              </>
            )}
          </Alert>
          {loadingProfile && (
            <Box role="status" sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
              <CircularProgress aria-label="Loading subscription options" />
            </Box>
          )}
          {!loadingProfile && profileError && (
            <Alert severity="warning">{profileError}</Alert>
          )}
          {!loadingProfile && !profileError && username && (
            <SubscriptionPanel
              panelId="quota-subscription-panel"
              username={username}
              onAccessGranted={onAccessGranted}
            />
          )}
        </Box>
      </DialogContent>
      <DialogActions>
        <Button type="button" onClick={onClose}>{closeLabel}</Button>
      </DialogActions>
    </Dialog>
  );
};

export default SubscriptionDialog;
